/**
@format
@flow
*/
import React, {
  useCallback, useState,
} from 'react';
import debounce from 'lodash.debounce';
import styled from 'styled-components';
import { Button } from '@ziynx/components/buttons';
import { Heading } from '@ziynx/components/typography';
import { withAppManager, getMeasures } from '@ziynx/components/utilities';
import Modal from '@ziynx/components/modals';
import { TextEditor } from '@krispy-web/text-editor';
import * as KrispyTokens from '@krispy-tokens/blue';
import Colors from '@ziynx/theme/colors';
import type { ManagedProps } from '@ziynx/flow-types/appManager';

export interface Message {
  htmlString?: string;
  jsonState?: string;
}

type Props = $ReadOnly<{|
  /**
   * Signal to open modal
   */
  open?: boolean,
  /**
   * Body
   */
  content?: string,
  /**
   * Handler for when submit button is pressed in the editor
   */
  onSubmit?: (message: Message) => void;
  /**
   * Handler for when content is updated in the editor
   */
  onChange?: (message: Message) => void;
  /**
   * Config object for button group of
   * 1 button destructive and 1 button primary
   */
  buttonGroup?: {
    primary: {
      children: any,
      onClick?: (message?: Message) => any,
    },
    secondary: {
      children: any,
      onClick?: () => any,
    },
  },

|}>;

const MODAL = {
  height: {
    mobileSmall: '100vh',
    mobile: '100vh',
    tablet: '100vh',
    default: '442px',
  },
  width: {
    mobileSmall: '100vw',
    mobile: '100vw',
    tablet: '100vw',
    default: '520px',
  },
};

const BUTTON_GROUP_WIDTH = {
  mobileSmall: '100%',
  mobile: '100%',
  tablet: '100%',
  default: '472px',
};

const BUTTON_SINGLE_WIDTH = {
  mobileSmall: '100%',
  mobile: '100%',
  tablet: '100%',
  default: '228px',
};

const StyledModal = styled(Modal)`
  padding: ${KrispyTokens.KrispySpacingElementsMdSmall}px;;
  box-sizing: border-box;
  border-radius: ${({ isNotDesktop }) => isNotDesktop && '0px'};
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: ${({ isNotDesktop }) => (isNotDesktop && 'space-between')};
  height: 100%;
  position: relative;
  text-align: center;
`;

const HeadingWrapper = styled.span`
  padding-bottom: var(--krispy-spacing-elements-md-large);
`;

const StyledHeading = styled(Heading)`
  display: -webkit-box; 
  -webkit-line-clamp: 3; 
  -webkit-box-orient: vertical; 
  text-overflow: ellipsis;
  overflow: hidden;
  max-height: ${KrispyTokens.KrispySpacingElementsLdXlarge}px;
`;

const ButtonGroupSection = styled.div`
  display: flex;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  width: ${({ viewport }) => getMeasures(BUTTON_GROUP_WIDTH, viewport)};
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0 var(--krispy-spacing-elements-hd-large) var(--krispy-spacing-elements-md-large) var(--krispy-spacing-elements-hd-large);
`;

const StyledButton = styled(Button)`
  ${({ lastChild, isMobile }) => !lastChild && isMobile && `margin-bottom: ${KrispyTokens.KrispySpacingElementsHdXlarge}px;`};
  ${({ marginRight, isMobile }) => marginRight && !isMobile && `margin-right: ${KrispyTokens.KrispySpacingElementsHdXlarge}px;`};
`;

const TextEditorWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  text-align: left;
  width: 100%;
  height: ${({ isNotDesktop }) => (isNotDesktop ? 'auto' : '100%')};
  --text-editor-max-height-mobile: 180px;
  --text-editor-max-height-tablet-up: 262px;
  
  > div {
    width: 100%;
    border: 1px solid transparent;
    border-radius: 16px;
    min-height: 82px;
    min-width: 254px;
    a {
      color: ${KrispyTokens.KrispyColorElementsAccentOndark};
    }

    p {
      margin: 0;
    }
    i {
      font-style: italic;
    }
    
    :global .strikethrough {
      text-decoration: line-through;
    }
    
    ul {
      margin: 0;
      font-family: inherit;
      padding-inline-start: calc(${KrispyTokens.KrispySpacingElementsHdLarge}px + 8px);
    }
    
    li {
      font-family: inherit;
    }

    &:focus-visible, &:focus {
      outline: none;
      border: none;
    }
  }
`;

const TextEditorModal = (props: ManagedProps<Props>): React$Node => {
  const {
    open, buttonGroup, viewport, content, onChange, onSubmit,
  } = props;

  const isNotDesktop = viewport !== 'desktop' && viewport !== 'desktopSmall';
  const isMobile = viewport !== 'desktop' && viewport !== 'desktopSmall' && viewport !== 'tablet';
  const [editorMessage, setEditorMessage] = useState(content);

  const MODAL_HEIGHT = getMeasures(MODAL?.height, viewport);

  const MODAL_WIDTH = getMeasures(MODAL?.width, viewport);

  const BUTTON_WIDTH = getMeasures(BUTTON_SINGLE_WIDTH, viewport);

  const _onChange = (message: Message) => {
    if (onChange) return onChange(message);
    /* Only update internal state if not intended to be tracked externally */
    setEditorMessage(message);
  };

  const _onClickSave = () => {
    if (buttonGroup?.primary?.onClick) buttonGroup?.primary?.onClick(editorMessage);
  };

  const _debouncedChangeHandler = useCallback(debounce(_onChange, 300), []);

  return (
    <StyledModal
      open={open}
      width={MODAL_WIDTH}
      height={isNotDesktop ? MODAL_HEIGHT : 'auto'}
      isNotDesktop={isNotDesktop}
    >
      <ModalContent isNotDesktop={isNotDesktop}>
        <ContentSection>
          <HeadingWrapper>
            <StyledHeading small color={Colors.GrayVeryDark}>
              {!content ? 'Add Note' : 'Edit Note'}
            </StyledHeading>
          </HeadingWrapper>
          <TextEditorWrapper>
            <TextEditor
              onChange={_debouncedChangeHandler}
              initialValue={content || ''}
              onSubmit={onSubmit || undefined}
              placeholderText="Type your note..."
            />
          </TextEditorWrapper>
        </ContentSection>
        <ButtonGroupSection
          isMobile={isMobile}
          isNotDesktop={isNotDesktop}
          viewport={viewport}
        >
          <StyledButton
            variant="primary"
            marginRight
            isMobile={isMobile}
            width={BUTTON_WIDTH}
            onClick={_onClickSave}
          >
            {buttonGroup?.primary?.children}
          </StyledButton>
          <StyledButton
            variant="secondary"
            isNotDesktop={isNotDesktop}
            inverted
            lastChild
            width={BUTTON_WIDTH}
            onClick={buttonGroup?.secondary?.onClick}
          >
            {buttonGroup?.secondary?.children}
          </StyledButton>
        </ButtonGroupSection>

      </ModalContent>
    </StyledModal>
  );
};

export default (withAppManager(TextEditorModal): React$ComponentType<ManagedProps<Props>>);
